import React, {useCallback, useEffect} from 'react';
import {Grid} from "@material-ui/core";
import {useFormik} from "formik";
import {ManuscriptService} from "../../../services/manuscript.service";
import {useDispatch, useSelector} from "react-redux";
import {AddBookFormStep1} from "./add-book-form-step1";
import {AddBookFormStep2} from "./add-book-form-step2";
import {StyledButton} from "../../ui-base/button";
import {toggleProgress, toggleLoader, setProgressValue, showSnackSuccess} from "../../../store/global/global-slice";
import {CategoryModel} from "../../../models/category.model";
import {RootState} from "../../../store/root-reducer";
import {ManuscriptDetailsModel} from "../../../models/manuscript-details.model";
import * as Yup from "yup";
import {useHistory, useParams} from "react-router-dom";
import styled from "styled-components";
import {Colors} from "../../../theme/colors";
import {BaseService} from "../../../services/base.service";
import {RequireInfo} from "../../ui-base/require-info";
import {useTranslation} from "react-i18next";

const AddBookSchema = Yup.object().shape({
    title: Yup.string().required(),
    tags: Yup.array().min(1).required(),
    mainCategory: Yup.string().required(),
    categories: Yup.array().min(1).required(),
    file: Yup.mixed().test('fileType', 'Unsupported File Format', value => value ? ['application/pdf', 'text/plain'].includes(value.type) : true),
    shortFile: Yup.mixed().test('fileType', 'Unsupported File Format', value => value ? ['application/pdf', 'text/plain'].includes(value.type) : true)
});

export const EditBookForm = () => {
    const [activeStep, setActiveStep] = React.useState(1);
    const [contentFile, setContentFile] = React.useState(null);
    const [contentFragmentFile, setContentFragmentFile] = React.useState(null);
    const [contentIllustration, setContentIllustration] = React.useState(null);
    const [contentCover, setContentCover] = React.useState(null);
    const [manuscript, setManuscript] = React.useState<ManuscriptDetailsModel>(null);
    const history = useHistory();
    const dispatch = useDispatch();
    const urlParams = useParams();
    const id: string = urlParams['id'] as string;
    const { t } = useTranslation(['author', 'common']);
    const themaCategories: CategoryModel[] = useSelector(
        (state: RootState) => state.categories.categories
    );

    const form = useFormik({
        initialValues: {
            title: '',
            categories: [],
            tags: [],
            file: null,
            shortFile: null,
            cover: null,
            illustration: null,
            description: '',
            summary: '',
            partOfManuscript: '',
            typeOfNarration: '',
            realLocation: '',
            fictionLocation: '',
            actionTime: '',
            readerAge: '',
            readerGender: '',
            readerType: '',
            readerAddInfo: '',
            mainCategory: '',
            showShortFile: false,
            showCover: false,
            showIllustration: false,
            availableForMarket: false
        },
        validationSchema: AddBookSchema,
        onSubmit: async values => {
            if(!checkFileError()) {
                dispatch(toggleLoader(true));
                const manuscriptRes = await ManuscriptService.editManuscript(
                    manuscript.id,
                    values.title,
                    values.categories,
                    values.tags,
                    values.description,
                    values.summary,
                    values.partOfManuscript,
                    values.typeOfNarration,
                    values.realLocation,
                    values.fictionLocation,
                    values.actionTime,
                    values.readerAge,
                    values.readerGender,
                    values.readerType,
                    values.readerAddInfo,
                    values.mainCategory,
                    values.availableForMarket
                );

                if (manuscriptRes.status) {
                    let file = values.file || '';
                    let fragmentFile = values.shortFile || '';
                    let cover = values.cover || '';
                    let illustration = values.illustration || '';

                    if (!file && manuscript.content_full && manuscript.content_full.file && !contentFile) {
                        file = '';
                    } else if (!file && manuscript.content_full && manuscript.content_full.file && contentFile) {
                        file = null;
                    }

                    if (!fragmentFile && manuscript.content_fragment && manuscript.content_fragment.file && !contentFragmentFile) {
                        fragmentFile = '';
                    } else if (!fragmentFile && manuscript.content_fragment && manuscript.content_fragment.file && contentFragmentFile) {
                        fragmentFile = null;
                    }

                    if (!cover && manuscript.cover && manuscript.cover.file && !contentCover) {
                        cover = '';
                    } else if (!cover && manuscript.cover && manuscript.cover.file && contentCover) {
                        cover = null;
                    }

                    if (!illustration && manuscript.illustration && manuscript.illustration.file && !contentIllustration) {
                        illustration = '';
                    } else if (!illustration && manuscript.illustration && manuscript.illustration.file && contentIllustration) {
                        illustration = null;
                    }

                    dispatch(toggleProgress(true));
                    const attachmentRes = await ManuscriptService.uploadFilesManuscript(
                        file,
                        fragmentFile,
                        cover,
                        illustration,
                        manuscriptRes.data.id,
                        (progress) => {
                            dispatch(setProgressValue(progress))
                        }
                    );
                    dispatch(toggleProgress(false));
                    //
                    if (attachmentRes) {
                        if (activeStep === 1) {
                            dispatch(showSnackSuccess(t('common:editSaved')));
                            goToNextStep();
                        }
                        if (activeStep === 2) {
                            dispatch(showSnackSuccess(t('common:editSaved')));
                            history.push(`/author/book/${attachmentRes.id}/`);
                        }
                    }

                    dispatch(toggleLoader(false));
                } else {
                    dispatch(toggleLoader(false));
                }
            }
        }
    });

    const loadManuscript = useCallback(async (id: string) => {
        dispatch(toggleLoader(true));

        const manuscript = await ManuscriptService.getManuscript(id);

        if (manuscript) {
            setManuscript(manuscript);
            form.setFieldValue('title', manuscript.title);
            form.setFieldValue('tags', manuscript.tags.filter((tag) => tag.tag_type === 'tag'));
            form.setFieldValue('description', manuscript.description);
            form.setFieldValue('summary', manuscript.summary);
            form.setFieldValue('partOfManuscript', manuscript.fragment);
            form.setFieldValue('mainCategory', BaseService.parseOldMainCategory(manuscript.main_category || ''));
            form.setFieldValue('availableForMarket', manuscript.available_for_manuscript_market);

            form.setFieldValue('typeOfNarration', manuscript.tags.find((tag) => tag.tag_type === 'typeOfNarration')?.name || '');
            form.setFieldValue('realLocation', manuscript.tags.find((tag) => tag.tag_type === 'realLocation')?.name || '');
            form.setFieldValue('fictionLocation', manuscript.tags.find((tag) => tag.tag_type === 'fictionLocation')?.name || '');
            form.setFieldValue('actionTime', manuscript.tags.find((tag) => tag.tag_type === 'actionTime')?.name || '');
            form.setFieldValue('readerAge', manuscript.tags.find((tag) => tag.tag_type === 'readerAge')?.name || '');
            form.setFieldValue('readerGender', manuscript.tags.find((tag) => tag.tag_type === 'readerGender')?.name || '');
            form.setFieldValue('readerType', manuscript.tags.find((tag) => tag.tag_type === 'readerType')?.name || '');
            form.setFieldValue('readerAddInfo', manuscript.tags.find((tag) => tag.tag_type === 'readerAddInfo')?.name || '');

            form.setFieldValue('categories', manuscript.categories.map((category) => {
                const foundCategory = themaCategories.find((cat) => cat.code === category.code);

                const children = themaCategories.filter((category) => category.parent_code === foundCategory.code);
                if (children && children.length > 0) {
                    return {
                        ...foundCategory,
                        id: foundCategory.id,
                        name: t('categoriesComponent.general'),
                        description: t('categoriesComponent.general'),
                        children: [],
                        code: foundCategory.code + '_GENERALCATEGORY',
                        parent_code: foundCategory.code
                    }
                } else {
                    return foundCategory;
                }
            }));

            setContentFile(BaseService.getFileName(manuscript.content_full?.file_purpose, manuscript.content_full?.file));
            setContentFragmentFile(BaseService.getFileName(manuscript.content_fragment?.file_purpose, manuscript.content_fragment?.file));
            form.setFieldValue('showShortFile', !!manuscript.content_fragment?.file);
            setContentIllustration(BaseService.getFileName(manuscript.illustration?.file_purpose, manuscript.illustration?.file));
            form.setFieldValue('showIllustration', !!manuscript.illustration?.file);
            setContentCover(BaseService.getFileName(manuscript.cover?.file_purpose, manuscript.cover?.file));
            form.setFieldValue('showCover', !!manuscript.cover?.file);
        }

        dispatch(toggleLoader(false));
    }, [dispatch]);

    useEffect(() => {
        if (id) {
            loadManuscript(id);
        }
    }, [id, loadManuscript]);

    const checkFileError = () => {
        if (form.touched.file && !form.values.file && !form.values.shortFile && !contentFile && !contentFragmentFile) {
            return true;
        } else {
            return false;
        }
    }

    const goToNextStep = () => {
        setActiveStep(activeStep + 1);
    }

    const goToPreviousStep = () => {
        setActiveStep(activeStep - 1);
    }

    return (
        <form onSubmit={form.handleSubmit} style={{width: '100%'}}>

            <Grid container direction="row" justifyContent="space-between" alignItems={"center"} spacing={2}>
                {activeStep === 2 &&
                <>
                    <Grid item>
                        <StyledTitle>{t('addBook.detailsInfo')}</StyledTitle>
                    </Grid>

                    <Grid item>
                        <StyledButton type="button" color={'secondary'} onClick={goToPreviousStep}>
                            {'< ' + t('addBook.back')}
                        </StyledButton>

                        <StyledButton type="submit" color={'violet'} style={{marginLeft: '10px'}}>{t('common:save')}</StyledButton>
                    </Grid>
                </>
                }

                {activeStep === 1 &&
                <>
                    <Grid item>
                        <StyledTitle>{t('addBook.generalInfo')}</StyledTitle>
                    </Grid>

                    <Grid item>
                        <StyledButton type="button" onClick={form.submitForm} color={'violet'}>
                            {t('addBook.next') + ' >'}
                        </StyledButton>
                    </Grid>
                </>
                }
            </Grid>

            {activeStep === 1 && <AddBookFormStep1 form={form} contentFile={contentFile} setContentFile={setContentFile} contentFragmentFile={contentFragmentFile} setContentFragmentFile={setContentFragmentFile} contentCover={contentCover} setContentCover={setContentCover} contentIllustration={contentIllustration} setContentIllustration={setContentIllustration} fileError={checkFileError()}/>}
            {activeStep === 2 && <AddBookFormStep2 form={form} />}

            <RequireInfo></RequireInfo>
        </form>
    )
}

const StyledTitle = styled('h5')`
  font-weight: 600;
  font-size: 1.4rem;
  color: ${Colors.lightViolet};
`;
